import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 16,
    backgroundColor: "#fff",
    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
  },
  titulo: {
    fontWeight: 700,
    color: "#111827",
  },
  campo: {
    marginBottom: theme.spacing(2.5),
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      backgroundColor: "#f9fafb",
      "& fieldset": {
        borderColor: "#e5e7eb",
      },
      "&:hover fieldset": {
        borderColor: "#2563eb",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#2563eb",
      },
    },
  },
  botoes: {
    display: "flex",
    justifyContent: "flex-end",
    padding: theme.spacing(2, 3),
  },
  botaoSalvar: {
    backgroundColor: "#16a34a",
    color: "#fff",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: 10,
    marginLeft: theme.spacing(1.5), // 👈 espaçamento entre botões
    "&:hover": {
      backgroundColor: "#15803d",
    },
  },
  botaoCancelar: {
    backgroundColor: "#ef4444",
    color: "#fff",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: 10,
    "&:hover": {
      backgroundColor: "#dc2626",
    },
  },
}));

export default function CardapioModal({
  aberto,
  onFechar,
  dia,
  visualizar,
  onSalvar,
  dados,
}) {
  const classes = useStyles();

  const [opcao1, setOpcao1] = useState("");
  const [descricao1, setDescricao1] = useState("");
  const [opcao2, setOpcao2] = useState("");
  const [descricao2, setDescricao2] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [salvando, setSalvando] = useState(false);

  // 🔁 Preenche dados do backend ao abrir o modal
  useEffect(() => {
    if (dados) {
      console.log("🧾 Dados recebidos no modal:", dados);
      setOpcao1(dados.opcao1_nome || "");
      setDescricao1(dados.opcao1_descricao || "");
      setOpcao2(dados.opcao2_nome || "");
      setDescricao2(dados.opcao2_descricao || "");
      setObservacoes(dados.observacoes || "");
    } else {
      setOpcao1("");
      setDescricao1("");
      setOpcao2("");
      setDescricao2("");
      setObservacoes("");
    }
  }, [dados, aberto]);

  // 💾 Salvar com validação e loading
  const handleSalvar = async () => {
    if (!opcao1.trim() && !opcao2.trim()) {
      toast.error("Preencha pelo menos uma opção do cardápio!");
      return;
    }

    const novo = {
      dia,
      opcao1_nome: opcao1,
      opcao1_descricao: descricao1,
      opcao2_nome: opcao2,
      opcao2_descricao: descricao2,
      observacoes,
    };

    try {
      setSalvando(true);
      await onSalvar(novo);
      toast.success("Cardápio salvo com sucesso!");
      onFechar();
    } catch (error) {
      console.error("❌ Erro ao salvar cardápio:", error);
      toast.error("Erro ao salvar cardápio");
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog
      open={aberto}
      onClose={onFechar}
      classes={{ paper: classes.dialogPaper }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h6" className={classes.titulo}>
          🍽️ {visualizar ? "Visualizar" : "Editar"} Cardápio — {dia}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {visualizar ? (
          <Box>
            {opcao1 && (
              <Box bgcolor="#e0f7fa" borderRadius={10} p={2} mb={2} boxShadow="0 2px 8px #14b8a622">
                <Typography style={{ fontWeight: 700, color: '#14b8a6', fontSize: 17, marginBottom: 4 }}>🍽️ Opção 1</Typography>
                {opcao1.split(/\r?\n|,|;/).map((item, idx) => (
                  <Typography key={idx} style={{ marginLeft: 8, fontSize: 15 }}>{item.trim()}</Typography>
                ))}
                {descricao1 && (
                  <Typography style={{ marginLeft: 8, fontSize: 14, color: '#2563eb', marginTop: 6 }}>{descricao1}</Typography>
                )}
              </Box>
            )}
            {opcao2 && (
              <Box bgcolor="#e0f7fa" borderRadius={10} p={2} mb={2} boxShadow="0 2px 8px #14b8a622">
                <Typography style={{ fontWeight: 700, color: '#14b8a6', fontSize: 17, marginBottom: 4 }}>🍽️ Opção 2</Typography>
                {opcao2.split(/\r?\n|,|;/).map((item, idx) => (
                  <Typography key={idx} style={{ marginLeft: 8, fontSize: 15 }}>{item.trim()}</Typography>
                ))}
                {descricao2 && (
                  <Typography style={{ marginLeft: 8, fontSize: 14, color: '#2563eb', marginTop: 6 }}>{descricao2}</Typography>
                )}
              </Box>
            )}
            {observacoes && (
              <Box mt={2} bgcolor="#f1f5f9" borderRadius={8} p={2}>
                {observacoes.split(/\r?\n|,|;/).map((item, idx) => (
                  <Typography key={idx} style={{ fontSize: 15 }}>{item.trim()}</Typography>
                ))}
              </Box>
            )}
          </Box>
        ) : (
          <Box mt={1.5}>
            <TextField
              label="Opção 1 (obrigatória)"
              fullWidth
              multiline
              minRows={2}
              maxRows={6}
              variant="outlined"
              className={classes.campo}
              value={opcao1}
              onChange={(e) => setOpcao1(e.target.value)}
              disabled={visualizar}
            />
            <TextField
              label="Descrição da Opção 1"
              fullWidth
              multiline
              minRows={2}
              maxRows={6}
              variant="outlined"
              className={classes.campo}
              value={descricao1}
              onChange={(e) => setDescricao1(e.target.value)}
              disabled={visualizar}
            />
            <TextField
              label="Opção 2"
              fullWidth
              multiline
              rows={2}
              variant="outlined"
              className={classes.campo}
              value={opcao2}
              onChange={(e) => setOpcao2(e.target.value)}
              disabled={visualizar}
            />
            <TextField
              label="Descrição da Opção 2"
              fullWidth
              multiline
              rows={2}
              variant="outlined"
              className={classes.campo}
              value={descricao2}
              onChange={(e) => setDescricao2(e.target.value)}
              disabled={visualizar}
            />
            <TextField
              label="Observações gerais"
              fullWidth
              multiline
              rows={2}
              variant="outlined"
              className={classes.campo}
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              disabled={visualizar}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions className={classes.botoes}>
        <Button
          onClick={onFechar}
          className={classes.botaoCancelar}
          variant="contained"
        >
          Fechar
        </Button>
        {!visualizar && (
          <Button
            onClick={handleSalvar}
            className={classes.botaoSalvar}
            variant="contained"
            disabled={salvando}
          >
            {salvando ? "Salvando..." : "Salvar"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
